import React, { useState, useEffect, useRef } from 'react';
import { Settings, Bookmark, BookmarkCheck, Share2, Download, Play, Pause, Minus, Plus, ChevronLeft, ChevronRight, X, Save, MessageSquare, Heart, Flag, ArrowLeft, WifiOff, Battery, MoreVertical, Star, StarOff } from 'lucide-react';

interface Article {
  id: string;
  title: string;
  content: string;
  author: string;
  publishedAt: Date;
  readTime: number;
  tags: string[];
  imageUrl?: string;
  likes: number;
  isLiked: boolean;
  isBookmarked: boolean;
  isStarred: boolean;
  isFlagged: boolean;
}

interface MobileReadingSettings {
  fontSize: 'small' | 'medium' | 'large' | 'xlarge';
  lineHeight: 'tight' | 'normal' | 'relaxed';
  fontFamily: 'serif' | 'sans' | 'mono';
  theme: 'light' | 'dark' | 'sepia' | 'night';
  width: 'narrow' | 'medium' | 'wide';
  autoScroll: boolean;
  autoScrollSpeed: number;
  hapticFeedback: boolean;
  swipeNavigation: boolean;
  voiceReading: boolean;
}

interface MobileReadingExperienceProps {
  article: Article;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
  onBookmark: (articleId: string, bookmarked: boolean) => void;
  onShare: (articleId: string) => void;
  onDownload: (articleId: string) => void;
  onLike: (articleId: string, liked: boolean) => void;
  onStar: (articleId: string, starred: boolean) => void;
  onFlag: (articleId: string, flagged: boolean) => void;
  hasNext: boolean;
  hasPrevious: boolean;
}

const MobileReadingExperience: React.FC<MobileReadingExperienceProps> = ({
  article,
  onClose,
  onNext,
  onPrevious,
  onBookmark,
  onShare,
  onDownload,
  onLike,
  onStar,
  onFlag,
  hasNext,
  hasPrevious
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState<string>('');
  const [isOffline, setIsOffline] = useState(false);
  const [batteryLevel, setBatteryLevel] = useState(100);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showMoreOptions, setShowMoreOptions] = useState(false);

  const [settings, setSettings] = useState<MobileReadingSettings>({
    fontSize: 'medium',
    lineHeight: 'normal',
    fontFamily: 'sans',
    theme: 'light',
    width: 'medium',
    autoScroll: false,
    autoScrollSpeed: 1,
    hapticFeedback: true,
    swipeNavigation: true,
    voiceReading: false
  });

  const contentRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef<number | null>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const startY = useRef<number>(0);
  const startX = useRef<number>(0);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Ensure body can scroll when reader is open
  useEffect(() => {
    document.body.style.overflow = 'unset';
    return () => {
      // Reset to default when component unmounts
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Check online status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Battery level (mock)
  useEffect(() => {
    const updateBatteryLevel = () => {
      if ('getBattery' in navigator) {
        (navigator as any).getBattery().then((battery: any) => {
          setBatteryLevel(Math.round(battery.level * 100));
        });
      }
    };
    
    updateBatteryLevel();
    const timer = setInterval(updateBatteryLevel, 30000);
    return () => clearInterval(timer);
  }, []);

  // Auto-scroll functionality
  useEffect(() => {
    if (settings.autoScroll && isPlaying) {
      autoScrollRef.current = window.setInterval(() => {
        if (contentRef.current) {
          const scrollAmount = settings.autoScrollSpeed * 0.5;
          contentRef.current.scrollTop += scrollAmount;
        }
      }, 16);
    } else {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
        autoScrollRef.current = null;
      }
    }

    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    };
  }, [settings.autoScroll, isPlaying, settings.autoScrollSpeed]);

  // Reading progress tracking
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const element = contentRef.current;
        const scrollTop = element.scrollTop;
        const scrollHeight = element.scrollHeight - element.clientHeight;
        const progress = (scrollTop / scrollHeight) * 100;
        setReadingProgress(Math.min(100, Math.max(0, progress)));
      }
    };

    const element = contentRef.current;
    if (element) {
      element.addEventListener('scroll', handleScroll);
      return () => element.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Auto-hide controls
  useEffect(() => {
    const handleTouchStart = () => {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    };

    document.addEventListener('touchstart', handleTouchStart);
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  // Swipe navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!settings.swipeNavigation) return;
    
    startY.current = e.touches[0].clientY;
    startX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!settings.swipeNavigation) return;
    
    const endY = e.changedTouches[0].clientY;
    const endX = e.changedTouches[0].clientX;
    const deltaY = startY.current - endY;
    const deltaX = startX.current - endX;
    
    // Horizontal swipe for navigation
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      if (deltaX > 0 && hasNext) {
        onNext();
      } else if (deltaX < 0 && hasPrevious) {
        onPrevious();
      }
    }
    
    // Vertical swipe for controls
    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 50) {
      if (deltaY > 0) {
        setShowControls(true);
      } else {
        setShowControls(false);
      }
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const adjustScrollSpeed = (direction: 'up' | 'down') => {
    const newSpeed = direction === 'up' 
      ? Math.min(5, settings.autoScrollSpeed + 0.5)
      : Math.max(0.5, settings.autoScrollSpeed - 0.5);
    
    setSettings(prev => ({ ...prev, autoScrollSpeed: newSpeed }));
  };

  const updateSetting = <K extends keyof MobileReadingSettings>(
    key: K, 
    value: MobileReadingSettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const getFontSize = () => {
    const sizes = { small: '14px', medium: '16px', large: '18px', xlarge: '20px' };
    return sizes[settings.fontSize];
  };

  const getLineHeight = () => {
    const heights = { tight: '1.4', normal: '1.6', relaxed: '1.8' };
    return heights[settings.lineHeight];
  };

  const getFontFamily = () => {
    const families = {
      serif: 'Georgia, "Times New Roman", serif',
      sans: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      mono: '"JetBrains Mono", "Fira Code", monospace'
    };
    return families[settings.fontFamily];
  };

  const getThemeClasses = () => {
    const themes = {
      light: 'bg-white text-gray-900',
      dark: 'bg-gray-900 text-gray-100',
      sepia: 'bg-amber-50 text-amber-900',
      night: 'bg-black text-gray-100'
    };
    return themes[settings.theme];
  };

  const getWidthClass = () => {
    const widths = {
      narrow: 'max-w-sm',
      medium: 'max-w-2xl',
      wide: 'max-w-4xl'
    };
    return widths[settings.width];
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`fixed inset-0 z-50 ${getThemeClasses()} transition-colors duration-300 flex flex-col`}>
      {/* Status Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-black/80 text-white text-xs px-4 py-1 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span>{formatTime(currentTime)}</span>
          {isOffline && <WifiOff className="w-3 h-3" />}
        </div>
        <div className="flex items-center gap-1">
          <Battery className="w-3 h-3" />
          <span>{batteryLevel}%</span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="fixed top-6 left-0 right-0 h-1 bg-blue-500 z-40">
        <div 
          className="h-full bg-blue-600 transition-all duration-300"
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      {/* Header */}
      <div className={`fixed top-7 left-0 right-0 z-40 transition-transform duration-300 ${
        showControls ? 'translate-y-0' : '-translate-y-full'
      }`}>
        <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex-1 min-w-0">
                  <h1 className="text-sm font-semibold truncate">{article.title}</h1>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    by {article.author} • {article.readTime} min
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => onBookmark(article.id, !article.isBookmarked)}
                  className={`p-2 rounded-lg transition-colors ${
                    article.isBookmarked 
                      ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  {article.isBookmarked ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                </button>

                <button
                  onClick={() => setShowMoreOptions(!showMoreOptions)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* More Options Menu */}
      {showMoreOptions && (
        <div className="fixed top-20 right-4 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2">
          <button
            onClick={() => onShare(article.id)}
            className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
          >
            <Share2 className="w-4 h-4" />
            Share
          </button>
          <button
            onClick={() => onDownload(article.id)}
            className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download
          </button>
          <button
            onClick={() => onStar(article.id, !article.isStarred)}
            className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
          >
            {article.isStarred ? <StarOff className="w-4 h-4" /> : <Star className="w-4 h-4" />}
            {article.isStarred ? 'Unstar' : 'Star'}
          </button>
          <button
            onClick={() => onFlag(article.id, !article.isFlagged)}
            className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
          >
            <Flag className="w-4 h-4" />
            Flag
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto pt-20 pb-24" style={{ minHeight: 0 }}>
        <div className={`mx-auto px-4 ${getWidthClass()}`}>
          <div
            ref={contentRef}
            className="prose prose-lg max-w-none"
            style={{
              fontSize: getFontSize(),
              lineHeight: getLineHeight(),
              fontFamily: getFontFamily(),
              minHeight: '100vh'
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {article.imageUrl && (
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-48 object-cover rounded-lg mb-6"
              />
            )}
            
            <div dangerouslySetInnerHTML={{ __html: article.content }} />
          </div>
        </div>
      </div>

      {/* Bottom Controls */}
      <div className={`fixed bottom-0 left-0 right-0 z-40 transition-transform duration-300 ${
        showControls ? 'translate-y-0' : 'translate-y-full'
      }`}>
        <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-4">
                <button
                  onClick={togglePlayPause}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                </button>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => adjustScrollSpeed('down')}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-gray-600 dark:text-gray-400 min-w-[2rem] text-center">
                    {settings.autoScrollSpeed}x
                  </span>
                  <button
                    onClick={() => adjustScrollSpeed('up')}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                <button
                  onClick={() => onLike(article.id, !article.isLiked)}
                  className={`p-2 rounded-lg transition-colors ${
                    article.isLiked 
                      ? 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Heart className={`w-4 h-4 ${article.isLiked ? 'fill-current' : ''}`} />
                </button>

                <button
                  onClick={() => setShowNotes(!showNotes)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <MessageSquare className="w-4 h-4" />
                </button>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  {Math.round(readingProgress)}%
                </span>
                <div className="w-20 h-1 bg-gray-200 dark:bg-gray-700 rounded-full">
                  <div 
                    className="h-full bg-blue-600 rounded-full transition-all duration-300"
                    style={{ width: `${readingProgress}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <button
                onClick={onPrevious}
                disabled={!hasPrevious}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>

              <button
                onClick={() => setIsSettingsOpen(true)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <Settings className="w-4 h-4" />
              </button>

              <button
                onClick={onNext}
                disabled={!hasNext}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {isSettingsOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-t-lg w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Reading Settings</h3>
                <button
                  onClick={() => setIsSettingsOpen(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-6">
              {/* Font Size */}
              <div>
                <label className="block text-sm font-medium mb-2">Font Size</label>
                <div className="flex gap-2">
                  {(['small', 'medium', 'large', 'xlarge'] as const).map((size) => (
                    <button
                      key={size}
                      onClick={() => updateSetting('fontSize', size)}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors ${
                        settings.fontSize === size
                          ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {size.charAt(0).toUpperCase() + size.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Theme */}
              <div>
                <label className="block text-sm font-medium mb-2">Theme</label>
                <div className="grid grid-cols-2 gap-2">
                  {(['light', 'dark', 'sepia', 'night'] as const).map((theme) => (
                    <button
                      key={theme}
                      onClick={() => updateSetting('theme', theme)}
                      className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                        settings.theme === theme
                          ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {theme.charAt(0).toUpperCase() + theme.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Mobile Features */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Haptic Feedback</label>
                  <button
                    onClick={() => updateSetting('hapticFeedback', !settings.hapticFeedback)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.hapticFeedback ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.hapticFeedback ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Swipe Navigation</label>
                  <button
                    onClick={() => updateSetting('swipeNavigation', !settings.swipeNavigation)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.swipeNavigation ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.swipeNavigation ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Auto-scroll</label>
                  <button
                    onClick={() => updateSetting('autoScroll', !settings.autoScroll)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.autoScroll ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.autoScroll ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notes Panel */}
      {showNotes && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-end justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-t-lg w-full max-h-[60vh]">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Notes</h3>
                <button
                  onClick={() => setShowNotes(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add your notes here..."
                className="w-full h-32 p-3 border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <button
                onClick={() => {/* Save notes */}}
                className="mt-4 w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Save className="w-4 h-4 inline mr-2" />
                Save Notes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileReadingExperience;
